import { isAppTarget } from '../../utils/target-scope.utils';
import type {
  GroupedSignal,
  SignalListGroupByField,
  SignalListGroupCount,
} from '@terra/shared-contracts-signals';
import type { SignalsCountByApplicationIdResponseDto } from '@terra/shared-contracts-signals';
import type { AiComponentTypeType } from '@terra/shared-schemas-orchestrator';
import {
  EMPTY_TARGET_SIGNAL_COUNTS,
  mapAppSignalCountsToLegacy,
  resolveSurfaceSignalEntityQuery,
  toSurfaceSignalListQueryFromPortalFilters,
} from './types';
import type {
  FindingsBulkFilter,
  FindingsBulkFilterPayload,
  TargetScopeParams,
  TargetScopedSignalCountsResult,
} from './types';
import type { surfaceApi } from './surface-api';
import { mapSurfaceSignalToGroupedSignal } from './surface-signal-mapper';

type SurfaceApi = typeof surfaceApi;

type TargetScopeApiDeps = {
  countFindingsByFilter: (
    payload: FindingsBulkFilterPayload,
  ) => Promise<number>;
  getSignalsCountByApplicationId: (
    applicationId: string,
    endpointId?: string,
    filters?: Record<string, unknown>,
    includeApplicationLevelGroup?: boolean,
  ) => Promise<SignalsCountByApplicationIdResponseDto | null>;
  getSignalsByApplication: (
    applicationId: string,
    options?: Record<string, unknown>,
  ) => Promise<{
    signals: GroupedSignal[];
    totalCount: number;
    groups?: SignalListGroupCount[];
  } | null>;
  countSurfaceSignals: SurfaceApi['countSurfaceSignals'];
  listSurfaceSignals: SurfaceApi['listSurfaceSignals'];
};

export function createTargetScopeApi(deps: TargetScopeApiDeps) {
  return {
    countFindingsByTarget: async ({
      targetId,
      filter = {},
      excludedIds = [],
    }: TargetScopeParams & {
      filter?: FindingsBulkFilter;
      excludedIds?: string[];
    }): Promise<number> =>
      deps.countFindingsByFilter({
        applicationId: targetId,
        filter,
        excludedIds,
      }),

    countSignalsByTarget: async ({
      targetId,
      targetType,
      endpointId,
      filters,
      includeApplicationLevelGroup,
      surfaceAssetEntityId,
      surfaceAssetKind,
    }: TargetScopeParams & {
      endpointId?: string;
      filters?: Record<string, unknown>;
      includeApplicationLevelGroup?: boolean;
      surfaceAssetEntityId?: string;
      surfaceAssetKind?: string;
    }): Promise<TargetScopedSignalCountsResult> => {
      if (isAppTarget(targetType)) {
        const result = await deps.getSignalsCountByApplicationId(
          targetId,
          endpointId,
          filters as Parameters<typeof deps.getSignalsCountByApplicationId>[2],
          includeApplicationLevelGroup,
        );

        if (!result) {
          return EMPTY_TARGET_SIGNAL_COUNTS;
        }

        return mapAppSignalCountsToLegacy(result);
      }

      const surfaceQuery = toSurfaceSignalListQueryFromPortalFilters(filters);
      const entityQuery = resolveSurfaceSignalEntityQuery({
        surfaceAssetEntityId,
        surfaceAssetKind,
        targetType,
      });
      const count = await deps.countSurfaceSignals({
        targetType,
        surfaceId: targetId,
        ...surfaceQuery,
        ...entityQuery,
      });

      return {
        total: count,
        counts: {
          ...EMPTY_TARGET_SIGNAL_COUNTS.counts,
          ALL: count,
        },
      };
    },

    listSignalsByTarget: async ({
      targetId,
      targetType,
      skip,
      limit,
      orderBy,
      groupBy,
      filters,
      endpointIds,
      assetIds,
      componentTypes,
      urlPatterns,
      includeApplicationLevelGroup,
      surfaceAssetEntityId,
      surfaceAssetKind,
    }: TargetScopeParams & {
      skip?: number;
      limit?: number;
      orderBy?: { field: string; order: 'asc' | 'desc' }[];
      groupBy?: SignalListGroupByField;
      filters?: Record<string, unknown>;
      endpointIds?: string[];
      assetIds?: string[];
      componentTypes?: AiComponentTypeType[];
      urlPatterns?: string[];
      includeApplicationLevelGroup?: boolean;
      surfaceAssetEntityId?: string;
      surfaceAssetKind?: string;
    }): Promise<{
      signals: GroupedSignal[];
      totalCount: number;
      groups?: SignalListGroupCount[];
    }> => {
      if (isAppTarget(targetType)) {
        const result = await deps.getSignalsByApplication(targetId, {
          pagination: { skip, limit, orderBy },
          groupBy,
          filters,
          endpointIds,
          assetIds,
          componentTypes,
          urlPatterns,
          includeApplicationLevelGroup,
        });

        return {
          signals: result?.signals ?? [],
          totalCount: result?.totalCount ?? 0,
          ...(result?.groups && { groups: result.groups }),
        };
      }

      const surfaceQuery = toSurfaceSignalListQueryFromPortalFilters(filters);
      const entityQuery = resolveSurfaceSignalEntityQuery({
        surfaceAssetEntityId,
        surfaceAssetKind,
        targetType,
      });
      const result = await deps.listSurfaceSignals({
        targetType,
        surfaceId: targetId,
        skip,
        limit,
        ...surfaceQuery,
        ...entityQuery,
      });

      return {
        signals: result.data.map(mapSurfaceSignalToGroupedSignal),
        totalCount: result.total,
      };
    },
  };
}
